import React, { useContext, useEffect, useRef } from "react";
// import "pwa-mmt-ui-header/ssr/header.css";
import PreloadImages from "./PreloadImages";

import {
  DesktopLayout,
  DomesticTrip,
  FlexComp,
  FormattedText,
  ImageComponent,
  LandingBenefits,
  LandingTestimonials,
  SecureTravOffer,
  SpanTag,
  TravellingTo,
} from "@tm/static";
import { ExperimentTrackingString, useBreakpointV2 } from "@tm/ui-widgets";
import { theme } from "@tm/utility/global";

import InsuranceBottomComp from "./InsuranceBottomComp";

import { getAnalytics } from "@tm/insurance/analytics";
import {
  initializePageTracking,
  trackEvent,
  triggerPageExit,
} from "@tm/insurance/analytics/xdm";
import {
  navigationActions,
  pageStack,
  PAGE_NAME_CONSTANTS,
} from "@tm/insurance/navigation";
import {
  getCookieByName,
  getPartnerOnClientSide,
  isMyPartner,
  replaceHistory,
} from "@tm/insurance/utils";
import { usePartnerContext } from "@tm/static/partnerContext";
import InsuranceLandingShimmer from "./InsuranceLandingShimmer";
import { LandingPageProps } from "./types";

import { PARTNER } from "@tm/isomorphic-utils";
import { usePlanType } from "@tm/ui";
import MyCash from "../../components/MyCash/MyCash";
import ProductInfoSection from "../../components/ProductInfoSection/ProductInfoSection";
import {
  HorizontalDivider,
  LandingPageRightWrapper,
} from "./InsuranceLanding.style";

const analytics = getAnalytics();

const InsuranceLanding = ({
  data,
  setActiveModal,
  query,
}: LandingPageProps) => {
  const context = usePartnerContext();
  const { type } = usePlanType();
  const experiment = useContext(ExperimentTrackingString) || "";
  const isLandingPageRender = useRef(false);

  if (!data) return <InsuranceLandingShimmer />;

  const {
    title,
    isRegionSupported,
    claimSettlementText,
    secureTravData,
    travellingTo,
    calendarDatesLimit,
    upcomingTrip,
    upcomingTripsV2,
    errorFields,
    offeredBy,
    footer,
    seoFooter,
    travelSearchBanner,
    nextPageRoute,
    chatBotInfo,
    productInfoBanner,
    myCashData,
    benefitsData,
    itineraryId,
    claimYourInsurance,
    crossSell,
    testimonials,
    seoContent,
  } = data;

  const device = useBreakpointV2();
  const partner = getPartnerOnClientSide();
  const isMyPartnerClient = isMyPartner();

  useEffect(() => {
    pageStack.push(PAGE_NAME_CONSTANTS.LANDING);
    if (itineraryId) {
      replaceHistory("itineraryId", itineraryId);
    }
    analytics?.trackPageLoadEvent({ pageName: "LANDING", experiment }, type);
    const { value = [] } =
      travellingTo?.travelSearchForm?.regionCountryList || {};
    const queryParams = new URLSearchParams(window.location.search);
    const selectedCountries = query?.countries || queryParams.get("countries");
    const selectedCountryCodes = selectedCountries?.split(",");
    const subpageName = queryParams.get("type") || query?.type;

    let totalCountryData = value?.map((data: any) => {
      if (selectedCountryCodes?.includes(data.code)) {
        return { ...data, isSelected: true };
      }
      return { ...data };
    });
    const selectedCountry = totalCountryData?.filter(
      (item: any) => item.isSelected
    );

    if (value?.length) {
      localStorage.setItem(
        "TRAVEL_INSURANCE_COUNTRIES",
        JSON.stringify(totalCountryData)
      );
    }

    if (!isLandingPageRender.current) {
      initializePageTracking(
        {
          page_context: {
            page_name: "landing",
            page_type: "landing",
            funnel_step: "landing",
            ...(subpageName && { subpage_name: subpageName }),
          },
          // experiment_details: {
          //   variant_keys: "44w",
          //   honoured_exp_list: [],
          // }
        },
        {
          countries: selectedCountry ? selectedCountry : [],
          journeyType: query?.segment || queryParams.get("segment"),
          travellers: parseInt(
            query?.travellers || queryParams.get("travellers") || "1"
          ),
          fromDateTime:
            query?.startDate || queryParams.get("startDate")
              ? parseInt(
                  query?.startDate || queryParams.get("startDate") || "0"
                )
              : 0,
          toDateTime:
            query?.endDate || queryParams.get("endDate")
              ? parseInt(query?.endDate || queryParams.get("endDate") || "0")
              : 0,
        }
      );
      isLandingPageRender.current = true;
    }

    return () => {
      triggerPageExit();
    };
  }, []);

  const goToMyOrders = () => {
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "my_orders_clicked",
      },
    });

    analytics?.trackLandingEvents(
      {
        actionType: "my_orders_clicked",
      },
      "pageView",
      type
    );

    const authCookie =
      getCookieByName("mmt-auth") || getCookieByName("OAUTH-GOIBIBO");

    if (authCookie) {
      if (data?.myOrderBtnLink) {
        window.location.href = data?.myOrderBtnLink;
      } else {
        navigationActions.navigateToOrderListing();
      }
    } else {
      setActiveModal?.("LOGIN");
    }
  };

  // Education modal tracking handlers
  const handleEducationModalShown = () => {
    analytics?.trackLandingEvents?.(
      { actionType: "educationModal_shown" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_shown",
      },
    });
  };
  const handleEducationModalNextClick = () => {
    analytics?.trackLandingEvents?.(
      { actionType: "educationModal_next_click" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_next_click",
      },
    });
  };
  const handleEducationModalSkipClick = () => {
    analytics?.trackLandingEvents?.(
      { actionType: "educationModal_skip_click" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "educationModal_skip_click",
      },
    });
  };
  const handleEducationModalPageShown = (pageId: string) => {
    analytics?.trackLandingEvents?.(
      { actionType: `educationModal_${pageId}_shown` },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: `educationModal_${pageId}_shown`,
      },
    });
  };

  const handleProductInfoKnowMoreClick = () => {
    analytics?.trackLandingEvents?.(
      { actionType: "productInfo_knowMore_clicked" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "productInfo_knowMore_clicked",
      },
    });
  };
  const handleProductInfoCardClick = () => {
    analytics?.trackLandingEvents?.(
      { actionType: "productInfo_card_clicked" },
      "pageView",
      type
    );
    trackEvent({
      event_detail: {
        event_type: "action",
        event_name: "component_cta-clicked",
        event_value: "productInfo_card_clicked",
      },
    });
  };

  return (
    <>
      <PreloadImages data={data} />
      <DesktopLayout
        pageName="LANDING"
        landingPageProp={type ? false : true}
        title={title}
        height={device == "lg" ? "720px" : ""}
        rightSectionHeight="unset"
        leftSectionHeight
        landingMargin={type ? false : true}
        goToMyOrders={goToMyOrders}
        chatBotInfo={chatBotInfo}
        showMyOrder={
          !travellingTo?.travellerInfoSection && partner !== PARTNER.MYBIZ
        }
        leftPositionSticky={type || partner === PARTNER.MYBIZ ? false : true}
        leftComp={
          <React.Fragment>
            <FlexComp
              margin={device == "sm" ? "0 0 0px" : context ? "-30px 0 0" : ""}
              alignItems="center"
              gap="6px"
            >
              {offeredBy ? (
                <FlexComp
                  flexDirection="column"
                  // alignItems="center"
                  margin="10px 0px 0px 0px"
                >
                  <FlexComp gap="4px">
                    {offeredBy?.title && (
                      <SpanTag fontWeight="bold">
                        <FormattedText value={offeredBy?.title} />
                      </SpanTag>
                    )}
                    {offeredBy?.icons?.map((url: string, index: number) => {
                      return (
                        <FlexComp gap="10px">
                          <ImageComponent
                            key={url}
                            height="22px"
                            width="auto"
                            src={url}
                            loading="eager"
                          />
                          {index !== offeredBy?.icons?.length - 1 && (
                            <HorizontalDivider
                              height="22px"
                              width="1px"
                              backgroundColor="#979797"
                            />
                          )}
                        </FlexComp>
                      );
                    })}
                  </FlexComp>
                  {offeredBy?.description && (
                    <SpanTag fontSize="xs">
                      <FormattedText value={offeredBy?.description} />
                    </SpanTag>
                  )}
                </FlexComp>
              ) : (
                <React.Fragment>
                  <ImageComponent
                    width="12px"
                    height="16px"
                    src={claimSettlementText?.icon}
                    loading="eager"
                  />
                  <SpanTag
                    fontSize="xs"
                    fontWeight="bold"
                    color={theme.blackText}
                  >
                    <FormattedText value={claimSettlementText.title} />
                  </SpanTag>
                </React.Fragment>
              )}
            </FlexComp>
            {device == "lg" && secureTravData && (
              <SecureTravOffer data={secureTravData} />
            )}
            {isMyPartnerClient && myCashData && <MyCash {...myCashData} />}
            {productInfoBanner && (
              <ProductInfoSection
                productInfoBanner={productInfoBanner}
                onEducationModalShown={handleEducationModalShown}
                onEducationModalNextClick={handleEducationModalNextClick}
                onEducationModalSkipClick={handleEducationModalSkipClick}
                onEducationModalPageShown={handleEducationModalPageShown}
                onKnowMoreClick={handleProductInfoKnowMoreClick}
                onProductCardClick={handleProductInfoCardClick}
              />
            )}
            {isMyPartnerClient && (
              <LandingBenefits benefitsDataList={benefitsData} />
            )}
          </React.Fragment>
        }
        rightComp={
          <LandingPageRightWrapper rightCompMargin={type ? true : false}>
            {upcomingTripsV2 && (
              <DomesticTrip
                title={upcomingTripsV2?.title}
                trips={upcomingTripsV2?.trips}
              ></DomesticTrip>
            )}
            <TravellingTo
              travelSearchBanner={travelSearchBanner}
              travellingToSection={travellingTo}
              upcomingTrip={upcomingTrip}
              calendarDatesLimit={calendarDatesLimit}
              nextPageRoute={nextPageRoute}
              errorFields={errorFields}
              query={query}
            />

            {device != "lg" && <SecureTravOffer data={secureTravData} />}
            {testimonials && (
              <LandingTestimonials
                customTitle={testimonials?.title}
                test={testimonials?.testimonialData}
              />
            )}
          </LandingPageRightWrapper>
        }
        bottomComp={
          <InsuranceBottomComp
            bottomCompData={data}
            footerInfo={seoFooter || footer}
          />
        }
      ></DesktopLayout>
    </>
  );
};

export default InsuranceLanding;
